import { Card } from "@/components/ui/card";
import { Mail, User, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";

const Impressum = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useLanguage();

  return (
    <main id="main-content" className="min-h-screen">
      <SEO
        title={t("seo.impressum.title")}
        description={t("seo.impressum.description")}
        keywords={t("seo.impressum.keywords").split(", ")}
      />
      <div className="container mx-auto px-2 sm:px-4 py-12">
        <Breadcrumbs />

        <div className="max-w-5xl mx-auto">
          <div className="mb-16 animate-fade-in">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">Impressum</h1>
            <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed">Legal Information</p>
          </div>

          <Card className="bg-gradient-card backdrop-blur-sm border border-primary/10 p-8 md:p-12 animate-scale-in space-y-8">
            {/* Angaben gemäß §5 TMG */}
            <section>
              <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <User className="h-6 w-6 text-primary" />
                Angaben gemäß §5 TMG
              </h2>
              <div className="space-y-2 text-muted-foreground">
                <p><strong>Verantwortlich für den Inhalt:</strong></p>
                <p>Kevin Rassner</p>
                <p className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  kevin.rassner@gmail.com
                </p>
              </div>
            </section>

            {/* Kontakt */}
            <section>
              <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <Mail className="h-6 w-6 text-primary" />
                Kontakt
              </h2>
              <div className="space-y-2 text-muted-foreground">
                <p className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  E-Mail: kevin.rassner@gmail.com
                </p>
              </div>
            </section>

            {/* EU-Streitschlichtung */}
            <section>
              <h2 className="text-2xl font-semibold mb-4">EU-Streitschlichtung</h2>
              <div className="space-y-2 text-muted-foreground">
                <p>
                  Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:
                </p>
                <p>
                  <a
                    href="https://ec.europa.eu/consumers/odr"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    https://ec.europa.eu/consumers/odr
                    <span className="sr-only"> (opens in a new tab)</span>
                  </a>
                </p>
                <p>
                  Unsere E-Mail-Adresse finden Sie oben im Impressum.
                </p>
              </div>
            </section>

            {/* Verbraucherstreitbeilegung/Universalschlichtungsstelle */}
            <section>
              <h2 className="text-2xl font-semibold mb-4">Verbraucherstreitbeilegung/Universalschlichtungsstelle</h2>
              <div className="space-y-2 text-muted-foreground">
                <p>
                  Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.
                </p>
              </div>
            </section>

            {/* Haftung für Inhalte */}
            <section>
              <h2 className="text-2xl font-semibold mb-4">Haftung für Inhalte</h2>
              <div className="space-y-2 text-muted-foreground">
                <p>
                  Als Diensteanbieter sind wir gemäß §7 Abs.1 TMG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich.
                  Nach §§8 bis 10 TMG sind wir als Diensteanbieter jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen
                  oder nach Umständen zu forschen, die auf rechtswidrige Tätigkeiten hinweisen.
                </p>
                <p>
                  Verpflichtungen zur Entfernung oder Sperrung der Nutzung von Informationen nach den allgemeinen Gesetzen bleiben hiervon unberührt.
                  Eine diesbezügliche Haftung ist jedoch erst ab dem Zeitpunkt der Kenntnis einer konkreten Rechtsverletzung möglich.
                  Bei Bekanntwerden von entsprechenden Rechtsverletzungen werden wir diese Inhalte umgehend entfernen.
                </p>
              </div>
            </section>

            {/* Haftung für Links */}
            <section>
              <h2 className="text-2xl font-semibold mb-4">Haftung für Links</h2>
              <div className="space-y-2 text-muted-foreground">
                <p>
                  Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen Einfluss haben.
                  Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen.
                  Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich.
                  Die verlinkten Seiten wurden zum Zeitpunkt der Verlinkung auf mögliche Rechtsverstöße überprüft.
                  Rechtswidrige Inhalte waren zum Zeitpunkt der Verlinkung nicht erkennbar.
                </p>
                <p>
                  Eine permanente inhaltliche Kontrolle der verlinkten Seiten ist jedoch ohne konkrete Anhaltspunkte einer Rechtsverletzung nicht zumutbar.
                  Bei Bekanntwerden von Rechtsverletzungen werden wir derartige Links umgehend entfernen.
                </p>
              </div>
            </section>

            {/* Urheberrecht */}
            <section>
              <h2 className="text-2xl font-semibold mb-4">Urheberrecht</h2>
              <div className="space-y-2 text-muted-foreground">
                <p>
                  Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen Urheberrecht.
                  Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen
                  der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers. Downloads und Kopien dieser Seite sind nur für den privaten,
                  nicht kommerziellen Gebrauch gestattet.
                </p>
                <p>
                  Soweit die Inhalte auf dieser Seite nicht vom Betreiber erstellt wurden, werden die Urheberrechte Dritter beachtet.
                  Insbesondere werden Inhalte Dritter als solche gekennzeichnet. Sollten Sie trotzdem auf eine Urheberrechtsverletzung aufmerksam werden,
                  bitten wir um einen entsprechenden Hinweis. Bei Bekanntwerden von Rechtsverletzungen werden wir derartige Inhalte umgehend entfernen.
                </p>
              </div>
            </section>

            {/* Footer Note */}
            <div className="pt-8 border-t border-primary/10 text-center text-sm text-muted-foreground">
              <p>Stand: {currentYear}</p>
            </div>
          </Card>
        </div>
      </div>
    </main>
  );
};

export default Impressum;
